import { useModal } from "@/context/ModalContext";
import { motion } from "framer-motion";
import { ArrowRight, Lock, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CONTACT } from "@/data/contact";

export function CTA() {
  const { t } = useTranslation();
  const { open } = useModal();

  return (
    <section id="cta" className="relative bg-dark-bg py-[96px] overflow-hidden">
      {/* Background grid - такой же как в Hero */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,173,181,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,173,181,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Gradient glow - как в Hero */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full bg-primary/25 blur-[140px] pointer-events-none" />

      {/* Дополнительный glow эффект */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative mx-auto max-w-[1200px] px-6 text-center">
        {/* Заголовок */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-white text-[36px] sm:text-[48px] lg:text-[56px] leading-[1.15] font-bold tracking-[-0.02em] max-w-3xl mx-auto"
        >
          {t("cta.title")}
        </motion.h2>

        {/* Подзаголовок */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-5 text-white/60 text-[16px] sm:text-[18px] leading-[1.7] max-w-xl mx-auto"
        >
          {t("cta.subtitle")}
          <br />
          <span className="text-white/40 text-[14px]">{t("cta.note")}</span>
        </motion.p>

        {/* Кнопки */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-9 flex flex-wrap justify-center gap-4"
        >
          <button
            onClick={open}
            className="group inline-flex items-center justify-center gap-2 h-[52px] px-8 rounded-lg bg-primary hover:bg-primary-dark text-white text-[16px] font-semibold shadow-button transition-all hover:scale-[1.02] hover:shadow-xl"
          >
            {t("cta.cta_primary")}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
          <a
            href={CONTACT.phone.href}
            className="inline-flex items-center justify-center gap-2 h-[52px] px-8 rounded-lg bg-transparent border border-white/20 hover:border-white/40 backdrop-blur-sm text-white/80 hover:text-white text-[16px] font-semibold transition-all hover:scale-[1.02]"
          >
            <Phone className="w-4 h-4 shrink-0" />
            {CONTACT.phone.display}
          </a>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4"
        >
          <div className="flex items-center gap-2 text-white/50 text-[13px] group hover:text-white/70 transition-colors">
            <Lock className="w-4 h-4 text-emerald-400 group-hover:scale-110 transition-transform" />
            <span>{t("cta.trust_payment")}</span>
          </div>

          <div className="w-px h-4 bg-white/10 hidden sm:block" />

          <div className="flex items-center gap-2 text-white/50 text-[13px] group hover:text-white/70 transition-colors">
            <Phone className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
            <span>{t("cta.trust_support")}</span>
          </div>

          <div className="w-px h-4 bg-white/10 hidden sm:block" />

          <div className="flex items-center gap-2 text-white/50 text-[13px] group hover:text-white/70 transition-colors">
            <MapPin className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
            <span>{t("cta.trust_local")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
